import React from 'react';
import { Grid, Box } from '@material-ui/core';
import CollapseProjectDetails from './CollapseProjectDetails';
import DocumentNavigationTabs from './DocumentNavigationTabs';
import { useStyles } from './styles/useStyles';
import { useProductDevelopment } from './hooks/useProductDevelopment';
import { useHandlers } from './handlers/useHandlers';
import { useProductDevelopmentHelpers } from './hooks/useProductDevelopmentHelpers';
import FilterSection from './components/FilterSection';
import ActionButtons from './components/ActionButtons';
import QuickSummarySection from './components/QuickSummarySection';
import SearchSection from './components/SearchSection';
import TableSection from './components/TableSection';
import RefreshSection from './components/RefreshSection';
import PaginationSection from './components/PaginationSection';
import ModalsSection from './components/ModalsSection';
import SnackbarSection from './components/SnackbarSection';
import BackDropLoading from '../models/BackDrop';

const ProductDevelopment = () => {
  const classes = useStyles();
  
  const {
    // State
    cmoSite,
    setCmoSite,
    deliverableType,
    setDeliverableType,
    searchCriteria,
    setSearchCriteria,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    initialCmoSite,
    initialDeliverableType,
    rowsPerPage,
    setRowsPerPage,
    filtersLoading,
    snackbar,
    setSnackbar,
    projects,
    quickSummaryStatuses,
    open,
    setOpen,
    selectedProject,
    setSelectedProject,
    openedProjectTab,
    setOpenedProjectTab,
    lastSyncTime,
    quickSummaryFilter,
    setQuickSummaryFilter,
    expanded,
    setExpanded,
    openActiveBatchesModal,
    setOpenActiveBatchesModal,
    updateStatusModalOpen,
    setUpdateStatusModalOpen,
    selectedProjectForUpdate,
    setSelectedProjectForUpdate,
    actionSelectValues,
    setActionSelectValues,
    selectedBatchNumbers,
    setSelectedBatchNumbers,
    selectedUnitProcedures,
    setSelectedUnitProcedures,
    selectedCampaigns,
    setSelectedCampaigns,
    selectedStages,
    setSelectedStages,
    selectedBatches,
    setSelectedBatches,
    selectedOperations,
    setSelectedOperations,
    cmoSitesList,
    deliverableTypesList,
    loading,
    apiError,
    // Functions
    loadDashboardData,
    setInitialCmoSite,
    setInitialDeliverableType,
    setQuickSummaryStatuses,
    setProjects,
    setLastSyncTime,
    setLoading,
    setFiltersLoading,
  } = useProductDevelopment();

  const handlers = useHandlers({
    cmoSite,
    deliverableType,
    searchCriteria,
    searchValue,
    currentPage,
    setCmoSite,
    setDeliverableType,
    setInitialCmoSite,
    setInitialDeliverableType,
    setQuickSummaryFilter,
    setSearchCriteria,
    setSearchValue,
    setCurrentPage,
    setProjects,
    setSelectedBatchNumbers,
    setSelectedUnitProcedures,
    setLastSyncTime,
    setQuickSummaryStatuses,
    setLoading,
    setFiltersLoading,
    setSnackbar,
    loadDashboardData,
  });

  const {
    handlePageChange,
    handleRowsPerPageChange,
    toggleExpand,
    handleOpenProjectTab,
    handleRefresh,
    totalPages,
    paginatedProjects,
  } = useProductDevelopmentHelpers({
    setSelectedCampaigns,
    setSelectedStages,
    setSelectedBatches,
    setSelectedOperations,
    setCurrentPage,
    setRowsPerPage,
    setOpen,
    setSelectedProject,
    setOpenedProjectTab,
    open,
    setLastSyncTime,
    projects,
    rowsPerPage,
    currentPage,
    loadDashboardData,
  });

  const selectedProjectData = selectedProject
    ? paginatedProjects.find(p => p.id === selectedProject)
    : null;

  // MFG Plant and Brand are UI-only filters (static, don't trigger API calls)
  const [mfgPlant, setMfgPlant] = React.useState('all');
  const [brand, setBrand] = React.useState('all');

  const handleClearAllFilters = () => {
    setMfgPlant('all');
    setBrand('all');
    handlers.handleClearFilters();
  };

  // Track applied filters vs current selections
  const [appliedCampaign, setAppliedCampaign] = React.useState(null);
  const [appliedStage, setAppliedStage] = React.useState(null);
  const [appliedBatch, setAppliedBatch] = React.useState(null);
  const [appliedOperation, setAppliedOperation] = React.useState('All');
  const [hasPendingFilterChanges, setHasPendingFilterChanges] = React.useState(false);

  // Check if filters have changed
  React.useEffect(() => {
    if (selectedProject) {
      const currentCampaign = selectedCampaigns[selectedProject] || null;
      const currentStage = selectedStages[selectedProject] || null;
      const currentBatch = selectedBatches[selectedProject]?.batchId || null;
      const currentOperation = selectedOperations[selectedProject] || 'All';
      
      // If no filters have been applied yet, check if we have initial selections
      if (appliedCampaign === null && appliedStage === null && appliedBatch === null && appliedOperation === 'All') {
        // Check if we have valid selections that haven't been applied
        setHasPendingFilterChanges(currentBatch !== null);
      } else {
        // Compare with applied values
        const campaignChanged = currentCampaign !== appliedCampaign;
        const stageChanged = currentStage !== appliedStage;
        const batchChanged = currentBatch !== appliedBatch;
        const operationChanged = currentOperation !== appliedOperation;
        setHasPendingFilterChanges(campaignChanged || stageChanged || batchChanged || operationChanged);
      }
    } else {
      setHasPendingFilterChanges(false);
    }
  }, [selectedProject, selectedCampaigns, selectedStages, selectedBatches, selectedOperations, appliedCampaign, appliedStage, appliedBatch, appliedOperation]);

  // Handle apply filters button
  const handleApplyFilters = () => {
    if (selectedProject) {
      const currentCampaign = selectedCampaigns[selectedProject] || null;
      const currentStage = selectedStages[selectedProject] || null;
      const currentBatch = selectedBatches[selectedProject]?.batchId || null;
      const currentOperation = selectedOperations[selectedProject] || 'All';
      
      setAppliedCampaign(currentCampaign);
      setAppliedStage(currentStage);
      setAppliedBatch(currentBatch);
      setAppliedOperation(currentOperation);
      setHasPendingFilterChanges(false);
      
      // API call will be triggered by CollapseProjectDetails when batchId/operation props change
    }
  };

  // Auto-apply default filters when project is first selected
  React.useEffect(() => {
    if (selectedProject) {
      const currentCampaign = selectedCampaigns[selectedProject] || null;
      const currentStage = selectedStages[selectedProject] || null;
      const currentBatch = selectedBatches[selectedProject]?.batchId || null;
      const currentOperation = selectedOperations[selectedProject] || 'All';
      
      // Auto-apply default selections when project is first selected (show all data by default)
      if (appliedCampaign === null && appliedStage === null && appliedBatch === null && appliedOperation === 'All') {
        setAppliedCampaign(currentCampaign);
        setAppliedStage(currentStage);
        setAppliedBatch(currentBatch);
        setAppliedOperation(currentOperation);
        setHasPendingFilterChanges(false);
      }
    } else {
      setAppliedCampaign(null);
      setAppliedStage(null);
      setAppliedBatch(null);
      setAppliedOperation('All');
      setHasPendingFilterChanges(false);
    }
  }, [selectedProject, selectedCampaigns, selectedStages, selectedBatches, selectedOperations]);

  // State for messages (shared with tabs)
  const [showMessages, setShowMessages] = React.useState(false);

  // When project selection changes, reset messages
  React.useEffect(() => {
    setShowMessages(false);
  }, [selectedProject]);

  const handleMessagesClick = () => {
    setShowMessages(true);
  };

  const handleBackToTabs = () => {
    setShowMessages(false);
  };

  return (
    <>
      <BackDropLoading />
      <Grid container justifyContent="center" style={{ overflowX: 'hidden', width: '100%' }}>
        <Grid item xs={12} style={{ width: '100%', overflowX: 'hidden' }}>
        <Grid
          container
          spacing={1}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'flex-end',
          }}
        >
          <FilterSection
            classes={classes}
            cmoSite={cmoSite}
            setCmoSite={setCmoSite}
            deliverableType={deliverableType}
            setDeliverableType={setDeliverableType}
            initialCmoSite={initialCmoSite}
            initialDeliverableType={initialDeliverableType}
            cmoSitesList={cmoSitesList}
            deliverableTypesList={deliverableTypesList}
            filtersLoading={filtersLoading}
            handleSaveFilters={handlers.handleSaveFilters}
            handleClearFilters={handleClearAllFilters}
            mfgPlant={mfgPlant}
            setMfgPlant={setMfgPlant}
            brand={brand}
            setBrand={setBrand}
          />

          <ActionButtons
            classes={classes}
            setOpenActiveBatchesModal={setOpenActiveBatchesModal}
          />
        </Grid>

        {!selectedProject && (
          <QuickSummarySection
            classes={classes}
            expanded={expanded}
            setExpanded={setExpanded}
            quickSummaryStatuses={quickSummaryStatuses}
            quickSummaryFilter={quickSummaryFilter}
            handleClearQuickSummary={handlers.handleClearQuickSummary}
            handleQuickStatus={handlers.handleQuickStatus}
            cmoSite={cmoSite}
            deliverableType={deliverableType}
            loading={loading || filtersLoading}
          />
        )}

        {selectedProject && (
          <Box style={{ marginTop: '1rem', marginBottom: '0px' }}>
            <DocumentNavigationTabs
              projectId={selectedProjectData?.projectId || null}
              campaignId={selectedCampaigns[selectedProject] || selectedProjectData?.projectDetails?.campaigns?.[0]?.campaignId || null}
              batchId={selectedBatches[selectedProject]?.batchId || null}
              operation={selectedOperations[selectedProject] || 'All'}
              onDownloadSuccess={(message) => {
                setSnackbar({
                  open: true,
                  message: message,
                  severity: 'success',
                });
              }}
              onDownloadError={(message) => {
                setSnackbar({
                  open: true,
                  message: message,
                  severity: 'error',
                });
              }}
            />
            <RefreshSection
              classes={classes}
              lastSyncTime={lastSyncTime}
              handleRefresh={handleRefresh}
            />
          </Box>
        )}

        {!selectedProject && (
          <SearchSection
            classes={classes}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleSearch={handlers.handleSearch}
            lastSyncTime={lastSyncTime}
            handleRefresh={handleRefresh}
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        )}

        <TableSection
          classes={classes}
          paginatedProjects={paginatedProjects}
          selectedProject={selectedProject}
          open={open}
          toggleExpand={toggleExpand}
          actionSelectValues={actionSelectValues}
          setActionSelectValues={setActionSelectValues}
          setSelectedProjectForUpdate={setSelectedProjectForUpdate}
          setUpdateStatusModalOpen={setUpdateStatusModalOpen}
          selectedBatches={selectedBatches}
          setSelectedBatches={setSelectedBatches}
          selectedOperations={selectedOperations}
          setSelectedOperations={setSelectedOperations}
          loading={loading || filtersLoading}
          apiError={apiError}
        />

        {selectedProjectData && (
          <Box style={{ marginTop: '0px' }}>
            <CollapseProjectDetails
              open={true}
              row={selectedProjectData}
              handleRefresh={handleRefresh}
              openedProjectTab={openedProjectTab[selectedProject]}
              handleOpenProjectTab={(val) => handleOpenProjectTab(selectedProject, val)}
              projectColumnConfig={null}
              batchId={appliedBatch || selectedBatches[selectedProject]?.batchId || null}
              operation={appliedOperation !== 'All' ? appliedOperation : (selectedOperations[selectedProject] || 'All')}
              showMessages={showMessages}
              onMessagesClick={handleMessagesClick}
              onBackToTabs={handleBackToTabs}
            />
          </Box>
        )}

        {!selectedProject && (
          <PaginationSection
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
          />
        )}
      </Grid>
      <ModalsSection
        updateStatusModalOpen={updateStatusModalOpen}
        setUpdateStatusModalOpen={setUpdateStatusModalOpen}
        selectedProjectForUpdate={selectedProjectForUpdate}
        setSelectedProjectForUpdate={setSelectedProjectForUpdate}
        openActiveBatchesModal={openActiveBatchesModal}
        setOpenActiveBatchesModal={setOpenActiveBatchesModal}
        cmoSite={cmoSite}
        deliverableType={deliverableType}
        onRefresh={loadDashboardData}
        setSnackbar={setSnackbar}
      />
      <SnackbarSection
        snackbar={snackbar}
        handleCloseSnackbar={handlers.handleCloseSnackbar}
      />
      </Grid>
    </>
  );
};

export default ProductDevelopment;
